import {
  formatNumber,
  type UsageApiClient,
  UsageChart,
  useUsageHistory,
} from '@xhubio-saas/core-frontend/plugin-usage'
import { Skeleton } from '@xhubio-saas/core-frontend/ui-components'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useEventTypeLabel } from '../../../lib/eventTypeLabel'
import { foldUsageSeries } from '../../../lib/usageSeries'
import { formatDay } from './billingState'

export interface UsageSectionProps {
  client: UsageApiClient
  /** The current billing period — the chart covers it day by day. */
  periodStart: Date
  periodEnd: Date
  locale: string
  /**
   * Event type → display name the page already has (`usage` bundle, quota catalog). Only the fallback after the
   * console-wide naming (`lib/eventTypeLabel.ts`), so dashboard and billing name a series the same way.
   */
  eventTypeLabels: Record<string, string>
}

/**
 * The „Usage" tab — calls of the current period per day from `getUsageHistory`, with the core `UsageChart`.
 *
 * 🔴 No overage card (plan M3): overage is not billed, a card estimating its cost would promise an invoice line that
 * never comes. ⚪ No rate-limit card: the console's usage client has no `getRateLimitStatus`, and E2 forbids a new
 * call — core `RateLimitCard` would render nothing.
 */
export function UsageSection({
  client,
  periodStart,
  periodEnd,
  locale,
  eventTypeLabels,
}: UsageSectionProps) {
  const { t } = useTranslation()
  // „Other" names the folded remainder exactly like the dashboard ring (core usage bundle).
  const { t: tUsage } = useTranslation('usage')
  const otherLabel = tUsage('todayCard.othersLabel', 'Other')
  const labelOf = useEventTypeLabel(eventTypeLabels)

  // The chart ends today, not at the period end — future days would be empty bars. End of the local day: the value
  // stays the same for the whole day (the range does not change on every render), and the label names today — the
  // UTC day end read as tomorrow east of Greenwich (seen 2026-10-11 in the browser).
  const todayEnd = new Date()
  todayEnd.setHours(23, 59, 59, 999)
  const endTime = Math.min(periodEnd.getTime(), todayEnd.getTime())
  const startTime = Math.min(periodStart.getTime(), endTime)
  const range = useMemo(
    () => ({
      pattern: '*',
      startDate: new Date(startTime),
      endDate: new Date(endTime),
      granularity: 'day' as const,
    }),
    [startTime, endTime]
  )
  const { history, isLoading } = useUsageHistory(client, range)

  // One entry per day and event type → one entry per day with a breakdown (pattern of core UsagePage), then the four
  // largest series of the period by name and the rest as ONE „Other" — the dashboard's rule (lib/usageSeries.ts), so
  // a format has the same colour here and there for the same range.
  const { chartData, seriesColors } = useMemo(() => {
    const byDay = new Map<
      string,
      { date: Date; count: number; breakdown: Record<string, number> }
    >()
    for (const entry of history) {
      const key = entry.date.toISOString()
      const bucket = byDay.get(key) ?? { date: entry.date, count: 0, breakdown: {} }
      if (entry.count > 0) {
        bucket.count += entry.count
        const label = labelOf(entry.pattern)
        bucket.breakdown[label] = (bucket.breakdown[label] ?? 0) + entry.count
      }
      byDay.set(key, bucket)
    }
    const perDay = [...byDay.values()]
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .map((bucket) => ({ ...bucket, pattern: 'total' }))
    const folded = foldUsageSeries(perDay, otherLabel)
    return { chartData: folded.data, seriesColors: folded.seriesColors }
  }, [history, labelOf, otherLabel])
  const total = chartData.reduce((sum, day) => sum + day.count, 0)

  return (
    <section className="min-w-0 space-y-3" data-testid="billing-usage">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="text-lg font-semibold">{t('billingPage.usage.title')}</h3>
        <p className="text-sm text-muted-foreground tabular-nums" data-testid="billing-usage-total">
          {t('billingPage.usage.summary', {
            start: formatDay(range.startDate, locale),
            end: formatDay(range.endDate, locale),
            total: formatNumber(total, locale),
          })}
        </p>
      </div>
      {/* No own frame: the tab panel is the white card already (BillingTabs) — a second frame was card-in-card. */}
      <div className="min-w-0">
        {isLoading ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <UsageChart
            data={chartData}
            seriesColors={seriesColors}
            height={220}
            granularity="day"
            noDataText={t('billingPage.usage.empty')}
            totalLabel={t('billingPage.usage.total')}
          />
        )}
      </div>
    </section>
  )
}
